import { ColumnType } from '@dynamika-shared/ui/Table/Table.types';
import { getRolesWithTooltip } from '@dynamika-shared/ui/Table/getValueWithTooltip';
import { User } from '@dynamika-shared/types/users';
import { UserInfo } from '@components/UserInfo/UserInfo';
import { StatusPill } from '@dynamika-shared/ui/StatusPill/StatusPill';
import { format } from 'date-fns';

import { OrganizationType } from '@dynamika-shared/types/inventory';
import { InstitutionInfo } from '@components/InstitutionInfo/InstitutionInfo';
import { Label } from '@dynamika-shared/ui/Label/Label';
import { UserRoles } from '@dynamika-shared/types/auth';
import { UserListActionsRenderer } from './UserListActionsRenderer';

const LENGTH_FOR_TOOLTIP = 20;
export enum DropdownOptionType {
  create = 'create',
  edit = 'edit',
  deactivate = 'deactivate',
  reactivate = 'reactivate',
  resendInvite = 'resendInvite',
}

export type MakeUsersColumnsType = {
  institutions: OrganizationType[];
  onUserClick: (user: User, procedure: DropdownOptionType) => void;
};

export function makeUsersColumns({
  institutions,
  onUserClick,
}: MakeUsersColumnsType): ColumnType<User>[] {
  return [
    {
      name: 'fullName',
      header() {
        return 'Full Name';
      },
      loading: index => {
        const width = Number(index) % 2 ? `w-[${20}rem]` : `w-[${17}rem]`;
        return (
          <div
            className={`h-2 ${width} animate-pulse rounded-[5px] bg-brightGray`}
          />
        );
      },
      getValue: u => {
        const isAdmin = u.roles.includes(UserRoles.ADMIN);
        return (
          <div className={'flex justify-between w-[calc(100%-1rem)]'}>
            <UserInfo
              fullName={`${u.firstName} ${u.lastName}`}
              email={u.email}
              enabled={true}
              className={'max-w-[calc(100%-6rem)]'}
              classNameText={'max-w-[calc(100%-4rem)]'}
            />
            {isAdmin && <Label>Admin</Label>}
          </div>
        );
      },
      hasSorting: false,
      thClassName: 'w-[30%] min-w-[30rem]',
    },
    {
      name: 'organizationName',
      header() {
        return 'Institution';
      },
      loading: index => {
        const width = Number(index) % 2 ? `w-[${17}rem]` : `w-[${13}rem]`;
        return (
          <div
            className={`h-2 ${width} animate-pulse rounded-[5px] bg-brightGray`}
          />
        );
      },
      getValue: row => {
        const institution = institutions.find(i => i.id === row.organizationId);
        return institution ? (
          <InstitutionInfo
            name={institution?.name}
            address={institution?.address}
          />
        ) : (
          '-'
        );
      },
      hasSorting: true,
      cellClassName: 'max-w-full min-w-0 grow',
      thClassName: 'min-w-[20rem] w-[22%]',
    },
    {
      name: 'lastActivity',
      header() {
        return 'Last Active';
      },
      loading: () => (
        <div
          className={'h-2 w-[10rem] animate-pulse rounded-[5px] bg-brightGray'}
        />
      ),
      getValue(row) {
        return (
          <div>
            {row.lastActivity
              ? format(new Date(row.lastActivity), 'dd-LLL-yyyy')
              : '-'}
          </div>
        );
      },
      hasSorting: true,
      thClassName: 'w-[11%]',
    },
    {
      name: 'roles',
      header: () => 'Role',
      loading: () => (
        <div
          className={'h-2 w-[10rem] animate-pulse rounded-[5px] bg-brightGray'}
        />
      ),
      getValue: getRolesWithTooltip(LENGTH_FOR_TOOLTIP),
      hasSorting: false,
      thClassName: 'w-[22%]',
    },
    {
      name: 'status',
      header() {
        return 'Status';
      },
      loading: index => {
        const width = Number(index) % 2 ? `w-[${13}rem]` : `w-[${10}rem]`;
        return (
          <div
            className={`h-2 ${width} animate-pulse rounded-md bg-brightGray`}
          />
        );
      },
      getValue(row) {
        return (
          <StatusPill
            status={row.status}
          />
        );
      },
      hasSorting: true,
    },

    {
      name: 'actions',
      header() {
        return 'Action';
      },
      loading: () => (
        <div className='h-3 w-3 animate-pulse rounded-md bg-brightGray' />
      ),
      getValue(user) {
        return (
          <div data-testid={`userAction-${user.id}`}>
            <UserListActionsRenderer user={user} onUserClick={onUserClick} />
          </div>
        );
      },
      alignToRight: true,
    },
  ];
}
